import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import { ProjectStatus } from 'providence-types';
import { ProjectInformation } from '@client/pages/project/components';
import { HttpService, ObjectComparatorService } from '@core/services';
import { ProjectHttpService } from '@core/services/http';
import { useDidUpdateEffect, useSaveChanged } from '@core/utils/hooks';
import { formDiffs, isValidForm } from '@core/utils/methods';
import {
  FormProps,
  ProjectCategory as ProjectCategoryInterface,
  ProjectUpdateRequestData,
} from '@models/interfaces';
import {
  getPaceTaskDifference,
  NotificationsLoader,
  paceTaskMapper,
  PaceTaskTable,
  Panel,
  ProjectCategory,
} from '@shared/components';
import { Input, PrimaryButton } from '@shared/modules';
import {
  CommonActions,
  ProjectActions,
  ProjectCategoriesActions,
} from '@store/actions';
import {
  PaceTasksSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';
import { DeleteProjectModal, HoldReasonModal } from './components';
import { ProjectInfo } from './models';

import './styles.scss';

const { useForm } = Form;

export const ProjectSummary: FC<FormProps> = ({
  isEditing = false,
  history,
  toggleEditing,
  permissions: { canRemoveProject, tableAccess, projectInformationFields },
}: PropsWithChildren<FormProps>) => {
  const projectService = useMemo(
    () => HttpService.getHttpRequests(ProjectHttpService),
    [],
  );

  const dispatch = useDispatch();

  const [form] = useForm();
  const [paceTaskForm] = useForm();
  const [notesForm] = useForm();

  const {
    currentProject,
    projectPaceTasks,
    projectSite,
    notes,
    currentProjectCategories,
    originalProjectCategories,
    projectCategoriesTableVisible,
  } = useSelector(ProjectSelectors.getProjectSummaryData);
  const arePaceTasksChanged = useSelector(
    PaceTasksSelectors.arePaseTasksChanged,
  );
  const roles = useSelector(UserSelectors.getUserRoles);

  const [holdReasonModalVisible, setReasonModalVisible] =
    useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo>({
    ...currentProject,
    FALocationNumber: projectSite.id,
  });
  const [objectsCompare, setObjectsCompare] = useState<boolean>();
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  useEffect(() => {
    notesForm.setFieldsValue({ ...notes });
  }, [notesForm, notes]);

  useEffect(
    () =>
      setProjectInfo({
        ...currentProject,
        FALocationNumber: projectSite.id,
      }),
    [currentProject, projectSite],
  );

  const toggleDeleteModalVisible = (): void =>
    setDeleteModalVisible((prevState: boolean) => !prevState);

  const getProjectData = (): Omit<ProjectUpdateRequestData, 'site'> => ({
    categories: currentProjectCategories.map(
      (category: ProjectCategoryInterface) => {
        const clonedCategory = category;

        delete clonedCategory.generatedId;

        const { projectAssignedDate, ...restValues } = clonedCategory;

        return {
          ...restValues,
          projectAssignedDate: moment(projectAssignedDate).toISOString(),
        };
      },
    ),
    ...formDiffs<any>(currentProject, {
      ...currentProject,
      ...projectInfo,
    }),
  });

  const onSubmit = async (): Promise<void> => {
    const [projectPaceTaskData, keys] = getPaceTaskDifference(
      projectPaceTasks,
      paceTaskForm,
      roles,
    );

    if ((await isValidForm(form)) && (await isValidForm(paceTaskForm, keys)))
      try {
        setSubmitDisabled(true);

        await projectService.updateProject(currentProject?.id, {
          ...getProjectData(),
          projectPaceTaskData,
        } as ProjectUpdateRequestData);

        toggleEditing?.();

        setObjectsCompare(true);
        dispatch(ProjectActions.setArePACETasksChanged.done(false));

        NotificationsLoader.notificationSuccess('Project has been updated!');
      } catch (e) {
        console.error(e);
      }

    setSubmitDisabled(false);
  };

  const onDelete = async (): Promise<void> => {
    try {
      await projectService.deleteProject(currentProject?.id);

      NotificationsLoader.notificationSuccess('Project has been deleted!');

      dispatch(ProjectCategoriesActions.setProjectCategoriesAction.done([]));
    } catch (e) {
      console.error(e);
    }

    history?.push('/project/browse');
  };

  const onCancel = (): void => {
    dispatch(
      ProjectCategoriesActions.setProjectCategoriesAction.done(
        originalProjectCategories,
      ),
    );
    setProjectInfo({
      ...currentProject,
      FALocationNumber: projectSite.id,
    });
    paceTaskForm.setFieldsValue(paceTaskMapper(projectPaceTasks));

    setObjectsCompare(true);
    dispatch(ProjectActions.setArePACETasksChanged.done(false));

    toggleEditing?.();
  };

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },

    [],
  );

  useDidUpdateEffect(() => {
    dispatch(
      CommonActions.setHasUnsubmittedData.done(
        [arePaceTasksChanged, !objectsCompare].some((item: boolean) => item),
      ),
    );
  }, [arePaceTasksChanged, objectsCompare]);

  useSaveChanged(isEditing, onSubmit, onCancel);

  const onProjectInfoChange = (): void => {
    const project = form.getFieldsValue();

    if (
      projectInfo.projectStatus !== ProjectStatus.Hold &&
      project.projectStatus === ProjectStatus.Hold
    ) {
      setReasonModalVisible(true);
      project.projectStatus = projectInfo.projectStatus;
      form.setFieldsValue({
        projectStatus: projectInfo.projectStatus,
      });
    }

    setObjectsCompare(
      ObjectComparatorService.objectsCompare(project, {
        ...currentProject,
        FALocationNumber: projectSite.id,
      }),
    );
    setProjectInfo(project as ProjectInfo);
  };

  const onOkHoldReasonHandle = (holdReason: string): void => {
    const project = {
      ...projectInfo,
      projectStatus: ProjectStatus.Hold,
      holdReason,
    };

    setObjectsCompare(
      ObjectComparatorService.objectsCompare(project, {
        ...currentProject,
        FALocationNumber: projectSite.id,
      }),
    );
    setProjectInfo(project);
    form.setFieldsValue({ projectStatus: ProjectStatus.Hold, holdReason });
    setReasonModalVisible(false);
  };

  return (
    <div className="prov-project-summary">
      <ProjectCategory editable={tableAccess} />
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isEditing,
          'categories-table-open': projectCategoriesTableVisible,
        })}
      >
        <div className="prov-project-summary__wrap">
          <div className="info-panel">
            <ProjectInformation
              className="info-panel__information"
              isEditing={isEditing}
              permissions={projectInformationFields}
              colSpan="24"
              form={form}
              data={projectInfo}
              onChange={onProjectInfoChange}
            />
            <Form
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
              className="info-panel__notes"
              form={notesForm}
            >
              <Panel header="Notes">
                <Input id="siteNotes" label="Site Notes" isEditing={false} />
                <Input
                  id="engineeringNotes"
                  label="Engineering Notes"
                  isEditing={false}
                />
                <Input
                  id="leasingNotes"
                  label="Leasing Notes"
                  isEditing={false}
                />
                <Input
                  id="projectPermittingNotes"
                  label="Project Permitting Notes"
                  isEditing={false}
                />
              </Panel>
            </Form>
          </div>
          <PaceTaskTable
            isEditing={isEditing}
            data={projectPaceTasks}
            form={paceTaskForm}
          />
        </div>
      </div>
      {isEditing && (
        <div className="prov-project-summary__btns">
          <div className="action-wrap">
            <PrimaryButton
              title="Submit"
              type="primary"
              disabled={submitDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton title="Cancel" type="default" onClick={onCancel} />
          </div>
          {canRemoveProject && (
            <PrimaryButton
              className="danger-btn"
              title="Delete the project"
              type="danger"
              onClick={toggleDeleteModalVisible}
            />
          )}
        </div>
      )}
      <DeleteProjectModal
        visible={deleteModalVisible}
        onCancel={toggleDeleteModalVisible}
        onOk={onDelete}
      />
      <HoldReasonModal
        visible={holdReasonModalVisible}
        onOk={onOkHoldReasonHandle}
        onCancel={(): void => setReasonModalVisible(false)}
      />
    </div>
  );
};
